/**
 * One subscription to "what is waiting for me" (CHARTER §7.194 ⑤).
 *
 * The server pushes an `approvals` frame -- the projection's own pending
 * list, whole -- when the stream opens and whenever a card moves, and
 * `App.tsx` hands every frame here. The nav badge, the approvals page, an
 * action's confirmation cards and a card's own buttons all read this one
 * list. Each used to fetch `/api/approvals` on its own clock or on every
 * frame, which was four copies of one fact, refreshed four ways.
 *
 * One read, and only before the first frame: a component that mounts before
 * the stream has spoken asks once, and everyone asking at that moment shares
 * the call. The page that also wants `history` and `held` -- which no frame
 * carries -- goes through the same `read`, so its pending list is this one
 * too. Nothing here is a second copy: the module holds the last list the
 * server sent, replaced whole by the next frame or read, written nowhere.
 */

import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type ApprovalCard, type HeldEffect } from "./api";

export interface ApprovalsReading { pending: ApprovalCard[]; history: ApprovalCard[]; held: HeldEffect[] }
type Listener = (pending: ApprovalCard[]) => void;

let latest: ApprovalCard[] | null = null;
let reading: Promise<ApprovalsReading> | null = null;
const listeners = new Set<Listener>();

export const approvalsFeed = {
  /** An `approvals` frame off the stream: the pending list, whole. */
  publish(pending: ApprovalCard[]): void {
    latest = pending;
    for (const listener of [...listeners]) listener(pending);
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  },
  /** The one read. Shared while in flight; its pending list is published like a frame. */
  read(): Promise<ApprovalsReading> {
    if (reading) return reading;
    const call = api.approvals().then(body => { approvalsFeed.publish(body.pending); return body; });
    reading = call;
    void call.catch(() => {}).finally(() => { if (reading === call) reading = null; });
    return call;
  },
  /** The last list the server sent, or null before it has said anything. */
  latest(): ApprovalCard[] | null { return latest; },
  /** Tests only: forget what the server said. */
  reset(): void { latest = null; reading = null; listeners.clear(); },
};

export interface PendingApprovals {
  /** null until the server has said anything. */
  pending: ApprovalCard[] | null;
  /** A read that failed. There is nothing to read after the first frame. */
  error: string | null;
  retry: () => void;
}

/**
 * The pending list as the server last sent it, and every frame after.
 *
 * `active` false subscribes to nothing and reads nothing -- an action that is
 * not held has no cards to look for.
 */
export function usePendingApprovals(active = true): PendingApprovals {
  const [pending, setPending] = useState<ApprovalCard[] | null>(() => (active ? latest : null));
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt(n => n + 1), []);
  useEffect(() => {
    if (!active) return;
    let live = true;
    const stop = approvalsFeed.subscribe(list => { if (live) { setPending(list); setError(null); } });
    if (latest !== null) setPending(latest);
    else approvalsFeed.read().catch(err => { if (live) setError(ApiError.fromUnknown(err).detail); });
    return () => { live = false; stop(); };
  }, [active, attempt]);
  return { pending: active ? pending : null, error, retry };
}
